import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xwvypggq';

const objetivos = [
  { value: 'perda-peso', label: 'Perda de peso' },
  { value: 'massa-muscular', label: 'Aumento de massa muscular' },
  { value: 'reabilitacao', label: 'Reabilitação física' },
  { value: 'outro', label: 'Outro' }
];

const AvaliacaoDialog = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    objetivo: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome.trim()) {
      newErrors.nome = 'Por favor preencha este campo.';
    }
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Por favor preencha este campo.';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Por favor preencha este campo.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Por favor insira um email válido.';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setFeedback({ type: '', message: '' });

    const objetivoLabel = (objetivos.find(o => o.value === formData.objetivo) || {}).label || '';

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: formData.nome,
          telemovel: formData.telefone,
          email: formData.email,
          objetivo: objetivoLabel || formData.objetivo,
          _subject: 'Nova marcação de avaliação física - Ultimate Fit'
        })
      });

      if (res.ok) {
        setFeedback({
          type: 'success',
          message: 'Pedido enviado com sucesso! Entraremos em contacto em breve.'
        });
        setFormData({ nome: '', telefone: '', email: '', objetivo: '' });
        setErrors({});
      } else {
        let detail = '';
        try {
          const data = await res.json();
          if (data && Array.isArray(data.errors) && data.errors.length) {
            detail = data.errors.map(err => err.message).join(' ');
          }
        } catch (_) { /* ignore */ }
        setFeedback({
          type: 'error',
          message: detail || 'Não foi possível enviar o pedido. Tente novamente.'
        });
      }
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Erro de ligação. Verifique a sua internet e tente novamente.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (feedback.message) {
      setFeedback({ type: '', message: '' });
    }
  };

  const objetivos = [
    { value: 'perda-peso', label: 'Perda de peso' },
    { value: 'massa-muscular', label: 'Aumento de massa muscular' },
    { value: 'reabilitacao', label: 'Reabilitação física' },
    { value: 'outro', label: 'Outro' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="p-0 bg-white overflow-hidden flex flex-col"
        style={{
          maxHeight: '90vh',
          maxHeight: '90dvh',
          width: 'calc(100vw - 24px)',
          maxWidth: '900px'
        }}
      >
        {/* Form Content - scrollável internamente */}
        <div className="overflow-y-auto p-5 sm:p-8 md:p-12 animate-uf-fade-in" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Header */}
          <div className="mb-7 pr-8">
            <DialogTitle className="text-2xl sm:text-3xl md:text-4xl text-black mb-2" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
              Faça a sua Avaliação Física gratuita!
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-base sm:text-lg uppercase" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "0.03em" }}>
              Juntos vamos atingir a sua melhor versão 💪
            </DialogDescription>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="avaliacao-form">
            {/* Three Fields in Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Nome */}
              <div>
                <Label htmlFor="nome" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Nome *
                </Label>
                <Input
                  id="nome"
                  name="nome"
                  data-testid="avaliacao-input-nome"
                  value={formData.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  className={`h-12 border-2 ${errors.nome ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                  placeholder="Insira o seu nome"
                />
                {errors.nome && (
                  <p className="text-red-500 text-sm mt-1">{errors.nome}</p>
                )}
              </div>

              {/* Telemóvel */}
              <div>
                <Label htmlFor="telefone" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Telemóvel *
                </Label>
                <Input
                  id="telefone"
                  name="telemovel"
                  type="tel"
                  data-testid="avaliacao-input-telemovel"
                  value={formData.telefone}
                  onChange={(e) => handleChange('telefone', e.target.value)}
                  className={`h-12 border-2 ${errors.telefone ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                  placeholder="Insira o seu telemóvel"
                />
                {errors.telefone && (
                  <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700 mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="avaliacao-input-email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`h-12 border-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:border-black`}
                  placeholder="Insira o seu email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Goal Selection - Radio Buttons */}
            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">
                Objetivo a alcançar
              </Label>
              <div className="space-y-3">
                {objetivos.map((objetivo) => (
                  <label
                    key={objetivo.value}
                    className="flex items-center space-x-3 cursor-pointer group"
                  >
                    <div className="relative flex items-center">
                      <input
                        type="radio"
                        name="objetivo"
                        value={objetivo.value}
                        checked={formData.objetivo === objetivo.value}
                        onChange={(e) => handleChange('objetivo', e.target.value)}
                        className="w-5 h-5 text-yellow-400 border-2 border-gray-300 focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                      />
                    </div>
                    <span className="text-gray-700 text-base group-hover:text-black transition-colors">
                      {objetivo.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Feedback (acima do botão para garantir visibilidade) */}
            {feedback.message && (
              <p
                role={feedback.type === 'error' ? 'alert' : 'status'}
                data-testid="avaliacao-feedback"
                className={`text-center text-sm mb-2 ${
                  feedback.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
              >
                {feedback.message}
              </p>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="avaliacao-submit-btn"
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black text-[20px] h-14 border-2 border-yellow-400 hover:border-yellow-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}
              >
                {submitting ? 'A ENVIAR...' : 'ENVIAR'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AvaliacaoDialog;
